import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import AdminTableToolbar from "./AdminTableToolbar";
import useAdminInfiniteList from "../../hooks/useAdminInfiniteList";
import { exportRowsToCSV } from "../../lib/csvExport";

/**
 * Admin Users tab — Sprint B v2.
 *
 * Replaces the legacy paginated list with infinite scroll, a richer
 * row (signup source, referrer name, last-seen-status badge), and a
 * dummy-account hide toggle. Desktop = real table; mobile (<md) =
 * stacked cards so we don't get a horizontal scrollbar on phones.
 */

function StatusDot({ status }) {
  const color =
    status === "green"
      ? "bg-emerald-500"
      : status === "yellow"
      ? "bg-amber-500"
      : "bg-rose-500";
  const label =
    status === "green" ? "פעיל היום" : status === "yellow" ? "השבוע" : "לא חזר";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function AdminUsersTab() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [includeDummy, setIncludeDummy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { items, total, loading, hasMore, sentinelRef, reset } = useAdminInfiniteList({
    fetchPage: api.adminUsersV2,
    params: { q, filter, include_dummy: includeDummy },
  });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.adminDeleteUser(confirmDelete.id);
      toast.success(`המשתמש ${confirmDelete.email} נמחק`);
      setConfirmDelete(null);
      reset();
      // Force a refetch by toggling and restoring a harmless filter
      // (the hook will fire because paramsKey changes).
      setQ((q) => q + " ");
      setTimeout(() => setQ((q) => q.trim()), 0);
    } catch (err) {
      toast.error("שגיאה במחיקה", {
        description: err.response?.data?.detail || "נסה שוב",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    exportRowsToCSV({
      filename: `torahya-users-${new Date().toISOString().slice(0, 10)}.csv`,
      columns: [
        { key: "name", label: "שם" },
        { key: "email", label: "אימייל" },
        { key: "signup_source", label: "מקור הרשמה" },
        { key: "referrer_name", label: "מפנה" },
        { key: "created_at", label: "תאריך הרשמה", fmt: (r) => formatDate(r.created_at) },
        { key: "last_seen_at", label: "פעילות אחרונה", fmt: (r) => formatDate(r.last_seen_at) },
        { key: "status", label: "סטטוס" },
      ],
      rows: items,
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <AdminTableToolbar
        q={q}
        onSearch={setQ}
        includeDummy={includeDummy}
        onToggleDummy={setIncludeDummy}
        onExportCSV={handleExport}
        exportDisabled={items.length === 0}
        testIdPrefix="admin-users"
      >
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            className="w-[180px]"
            data-testid="admin-users-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל המשתמשים</SelectItem>
            <SelectItem value="organic">ישירים</SelectItem>
            <SelectItem value="referral">מהפניה</SelectItem>
          </SelectContent>
        </Select>
      </AdminTableToolbar>

      {/* Result count */}
      <div className="text-xs text-muted-foreground">
        מציג {items.length} מתוך {total}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block rounded-md border border-border/60 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-card/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-right p-3">שם / אימייל</th>
              <th className="text-right p-3">מקור</th>
              <th className="text-right p-3">הצטרף</th>
              <th className="text-right p-3">פעילות</th>
              <th className="text-right p-3">סטטוס</th>
              <th className="text-right p-3 w-20" />
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr
                key={u.id}
                className="border-t border-border/30 hover:bg-card/40"
                data-testid={`user-row-${u.id}`}
              >
                <td className="p-3">
                  <div className="font-heading text-foreground">
                    {u.name || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground" dir="ltr">
                    {u.email}
                  </div>
                </td>
                <td className="p-3 text-muted-foreground">
                  {u.signup_source}
                  {u.referrer_name && (
                    <div className="text-xs text-gold/80">
                      ע"י {u.referrer_name}
                    </div>
                  )}
                </td>
                <td className="p-3 text-muted-foreground">{formatDate(u.created_at)}</td>
                <td className="p-3 text-muted-foreground">{formatDate(u.last_seen_at)}</td>
                <td className="p-3">
                  <StatusDot status={u.status} />
                </td>
                <td className="p-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setConfirmDelete(u)}
                    data-testid={`delete-user-${u.id}`}
                    className="h-8 w-8 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-3">
        {items.map((u) => (
          <div
            key={u.id}
            className="rounded-md border border-border/60 bg-card/40 p-4"
            data-testid={`user-card-${u.id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-heading text-foreground truncate">
                  {u.name || "—"}
                </div>
                <div className="text-xs text-muted-foreground truncate" dir="ltr">
                  {u.email}
                </div>
              </div>
              <StatusDot status={u.status} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>מקור: {u.signup_source}</div>
              <div>הצטרף: {formatDate(u.created_at)}</div>
              {u.referrer_name && (
                <div className="col-span-2 text-gold/80">ע"י {u.referrer_name}</div>
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDelete(u)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                מחק
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Sentinel + loading */}
      <div ref={sentinelRef} className="h-8 flex items-center justify-center">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {!loading && !hasMore && items.length > 0 && (
          <p className="text-xs text-muted-foreground">— הגעת לסוף הרשימה —</p>
        )}
        {!loading && items.length === 0 && (
          <p className="text-sm text-muted-foreground py-6">לא נמצאו משתמשים.</p>
        )}
      </div>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>למחוק את {confirmDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              פעולה לא הפיכה. כל ההערות, ההדגשות והסל של המשתמש יימחקו.
              תרומות שביצע יעברו לסטטוס "אנונימי".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>בטל</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "מחק"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
